import { Link } from 'react-router-dom';
import { AlertTriangle, BookOpen, Plug, Search, Target, TrendingUp } from 'lucide-react';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import type { CommandCenter, SeoQueryRow } from '@/services/marketingIntegrations.service';
import { bucketQueriesByIntent, classifyQueryIntent, formatNumber, type IntentBucket } from './derive';

interface Props {
  cc: CommandCenter;
}

function clickValueAt(impressions: number, ctr = 0.05): number {
  return Math.round(impressions * ctr);
}

const INTENT_TONE: Record<IntentBucket['intent'], 'success' | 'info' | 'warning' | 'neutral'> = {
  product: 'success',
  comparison: 'warning',
  educational: 'info',
  navigational: 'neutral',
  informational: 'neutral',
};

export default function SeoTab({ cc }: Props) {
  const organic = cc.organic;
  const gsc = cc.gsc;
  const organicConnected = organic?.ok === true;
  const gscConnected = gsc?.ok === true;

  if (!organicConnected && !gscConnected) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Search className="h-8 w-8 text-slate-400" />}
          title="No SEO data sources connected"
          description="Connect Google Analytics 4 + Google Search Console to see organic traffic, query intent, and ranking opportunities."
          action={{ label: 'Connect a provider', onClick: () => { window.location.href = '/admin/marketing-integrations'; } }}
        />
      </div>
    );
  }

  const queries = gsc?.topQueries ?? [];
  const buckets = bucketQueriesByIntent(queries);

  // Page-1 low-CTR title fixes (pos 1-10, CTR < 2%, ≥200 impr)
  const titleFixes: SeoQueryRow[] = queries
    .filter((q) => q.position >= 1 && q.position <= 10 && q.ctr < 2 && q.impressions >= 200)
    .sort((a, b) => b.impressions - a.impressions);

  // Page-2 quick-wins (pos 11-20, ≥100 impr)
  const quickWins: SeoQueryRow[] = queries
    .filter((q) => q.position > 10 && q.position <= 20 && q.impressions >= 100)
    .sort((a, b) => b.impressions - a.impressions);

  // Educational queries
  const educational: SeoQueryRow[] = queries
    .filter((q) => classifyQueryIntent(q.query) === 'educational')
    .sort((a, b) => b.impressions - a.impressions)
    .slice(0, 15);

  const missing: string[] = [];
  if (!organicConnected) missing.push('Google Analytics');
  if (!gscConnected) missing.push('Search Console');

  return (
    <div className="space-y-6 p-6">
      {missing.length > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span><strong>{missing.join(' & ')}</strong> not connected — section will be partial.</span>
          <Link to="/admin/marketing-integrations" className="ml-auto">
            <Button variant="ghost" size="sm"><Plug className="h-4 w-4 mr-1" />Manage</Button>
          </Link>
        </div>
      )}

      {/* KPIs */}
      <MetricCardGrid columns={4}>
        <StatCard
          label="Organic Sessions"
          value={organicConnected ? formatNumber(organic?.sessions ?? 0) : '—'}
          subtitle="GA4 · last 30 days · google/organic"
          tone="amber"
          icon={<Search className="h-4 w-4" />}
        />
        <StatCard
          label="Organic Conversions"
          value={organicConnected ? formatNumber(organic?.conversions ?? 0) : '—'}
          subtitle={organicConnected && (organic?.bounceRate ?? 0) > 0 ? `Bounce ${(organic?.bounceRate ?? 0).toFixed(1)}%` : 'Tracked in GA4'}
          tone="green"
          icon={<Target className="h-4 w-4" />}
        />
        <StatCard
          label="GSC Clicks"
          value={gscConnected ? formatNumber(gsc?.totalClicks ?? 0) : '—'}
          subtitle={gscConnected ? `${formatNumber(gsc?.totalImpressions ?? 0)} impressions` : 'Search Console required'}
          tone="blue"
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <StatCard
          label="Average Position"
          value={gscConnected ? (gsc?.avgPosition ?? 0).toFixed(1) : '—'}
          subtitle={gscConnected ? `${(gsc?.avgCtr ?? 0).toFixed(2)}% CTR` : 'Search Console required'}
          tone="violet"
          icon={<TrendingUp className="h-4 w-4" />}
        />
      </MetricCardGrid>

      {/* Intent buckets */}
      {gscConnected && buckets.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Query intent buckets</CardTitle></CardHeader>
          <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 text-left font-semibold">Intent</th>
                    <th className="px-3 py-2.5 text-right font-semibold"># queries</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Impressions</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Clicks</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Avg CTR</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Avg pos</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Revenue significance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {buckets.map((b) => (
                    <tr key={b.intent} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <Badge variant={INTENT_TONE[b.intent]} size="sm">{b.label}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{b.queries.length}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(b.totalImpressions)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(b.totalClicks)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{b.avgCtr.toFixed(2)}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{b.avgPosition.toFixed(1)}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">{revenueNote(b.intent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Title-fix table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-4 w-4 text-amber-600" />
            Page-1 low-CTR — title-tag fixes
          </CardTitle>
        </CardHeader>
        <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
          {!gscConnected ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">Connect Search Console to surface title-tag fixes.</div>
          ) : titleFixes.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">No page-1 rankings under 2% CTR with significant impressions yet.</div>
          ) : (
            <SeoTable rows={titleFixes} action="Rewrite title for click-thru" />
          )}
        </CardBody>
      </Card>

      {/* Quick-wins table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            Page-2 quick wins — one content push to page 1
          </CardTitle>
        </CardHeader>
        <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
          {!gscConnected ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">Connect Search Console to find quick wins.</div>
          ) : quickWins.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">No page-2 keywords with 100+ impressions yet.</div>
          ) : (
            <SeoTable rows={quickWins} action="Refresh content + internal links" />
          )}
        </CardBody>
      </Card>

      {/* Educational content table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-blue-600" />
            Educational queries — content opportunities
          </CardTitle>
        </CardHeader>
        <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
          {!gscConnected ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">Connect Search Console to plan content.</div>
          ) : educational.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">No educational-intent queries detected.</div>
          ) : (
            <SeoTable rows={educational} action={(row) => contentActionFor(row)} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function SeoTable({ rows, action }: { rows: SeoQueryRow[]; action: string | ((r: SeoQueryRow) => string) }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5 text-left font-semibold">Query</th>
            <th className="px-3 py-2.5 text-right font-semibold">Impr.</th>
            <th className="px-3 py-2.5 text-right font-semibold">Clicks</th>
            <th className="px-3 py-2.5 text-right font-semibold">CTR</th>
            <th className="px-3 py-2.5 text-right font-semibold">Pos.</th>
            <th className="px-3 py-2.5 text-right font-semibold">@ 5% CTR</th>
            <th className="px-3 py-2.5 text-left font-semibold">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.slice(0, 20).map((q) => (
            <tr key={q.query} className="hover:bg-slate-50">
              <td className="px-4 py-2.5 text-slate-900 font-medium max-w-[260px] truncate" title={q.query}>{q.query}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(q.impressions)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(q.clicks)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{q.ctr.toFixed(1)}%</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{q.position.toFixed(1)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{formatNumber(clickValueAt(q.impressions))}</td>
              <td className="px-3 py-2.5 text-xs text-slate-700">{typeof action === 'function' ? action(q) : action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function contentActionFor(q: SeoQueryRow): string {
  if (q.position <= 10) return 'Add FAQ block + improve title';
  if (q.position <= 20) return 'Long-form refresh + internal links';
  return 'Net-new pillar article';
}

function revenueNote(intent: IntentBucket['intent']): string {
  switch (intent) {
    case 'product': return 'Bottom-funnel — closest to revenue.';
    case 'comparison': return 'Decision-stage — high commercial weight.';
    case 'educational': return 'Top-funnel — feeds retargeting + lists.';
    case 'navigational': return 'Existing demand — protect with brand SEO.';
    case 'informational': return 'Awareness — ranks build authority.';
  }
}
